import type {
  AdminWebsiteWaitlistItem,
  AdminWebsiteWaitlistSummary,
  PaginationView,
  WebsiteWaitlistStatus,
} from "@actale/contracts";
import { useCallback, useEffect, useMemo, useState, type FormEvent, type ReactNode } from "react";
import { api, ApiError } from "./api.js";
import { formatWaitlistDate, WEBSITE_WAITLIST_STATUS_LABELS } from "./website-waitlist-utils.js";

const EMPTY_SUMMARY: AdminWebsiteWaitlistSummary = { total: 0, today: 0, last7Days: 0, pending: 0 };
const EMPTY_PAGINATION: PaginationView = { page: 1, pageSize: 20, total: 0, totalPages: 0 };
const STATUS_OPTIONS = Object.entries(WEBSITE_WAITLIST_STATUS_LABELS) as Array<[WebsiteWaitlistStatus, string]>;

function WaitlistError({ message }: { message: string }): ReactNode {
  return <div className="error-notice waitlist-error" role="alert"><span aria-hidden="true">!</span>{message}</div>;
}

export function WebsiteWaitlistPage(): ReactNode {
  const [items, setItems] = useState<AdminWebsiteWaitlistItem[]>([]);
  const [summary, setSummary] = useState(EMPTY_SUMMARY);
  const [pagination, setPagination] = useState(EMPTY_PAGINATION);
  const [query, setQuery] = useState("");
  const [status, setStatus] = useState<WebsiteWaitlistStatus | "">("");
  const [page, setPage] = useState(1);
  const [selectedId, setSelectedId] = useState("");
  const [editStatus, setEditStatus] = useState<WebsiteWaitlistStatus>("PENDING");
  const [note, setNote] = useState("");
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [saved, setSaved] = useState(false);
  const [error, setError] = useState("");
  const [copied, setCopied] = useState(false);

  const selected = useMemo(
    () => items.find((item) => item.id === selectedId) ?? null,
    [items, selectedId],
  );
  const hasChanges = selected !== null && (
    editStatus !== selected.status || note.trim() !== selected.note
  );

  const load = useCallback(async () => {
    setLoading(true);
    setError("");
    try {
      const result = await api.websiteWaitlist({ q: query, status, page, pageSize: 20 });
      setItems(result.data);
      setSummary(result.summary);
      setPagination(result.pagination);
      setSelectedId((current) => result.data.some((item) => item.id === current)
        ? current
        : (result.data[0]?.id ?? ""));
    } catch (caught) {
      setError(caught instanceof ApiError ? caught.message : "无法读取内测申请");
    } finally {
      setLoading(false);
    }
  }, [page, query, status]);

  useEffect(() => {
    const timer = window.setTimeout(() => void load(), 180);
    return () => window.clearTimeout(timer);
  }, [load]);

  useEffect(() => {
    if (!selected) return;
    setEditStatus(selected.status);
    setNote(selected.note);
    setCopied(false);
  }, [selected]);

  async function save(event: FormEvent): Promise<void> {
    event.preventDefault();
    if (!selected) return;
    setSaving(true);
    setSaved(false);
    setError("");
    try {
      const updated = await api.updateWebsiteWaitlist(selected.id, {
        expectedUpdatedAt: selected.updatedAt,
        status: editStatus,
        note,
      });
      setItems((current) => current.map((item) => item.id === updated.id ? updated : item));
      await load();
      setSaved(true);
    } catch (caught) {
      setError(caught instanceof ApiError ? caught.message : "保存跟进记录失败");
    } finally {
      setSaving(false);
    }
  }

  async function copyEmail(): Promise<void> {
    if (!selected) return;
    try {
      await navigator.clipboard.writeText(selected.email);
      setCopied(true);
    } catch {
      setError("浏览器未允许复制，请手动选择邮箱地址");
    }
  }

  return (
    <div className="waitlist-page">
      <div className="page-heading waitlist-heading">
        <div>
          <p className="eyebrow">WEBSITE OPERATIONS</p>
          <h1>内测申请</h1>
          <p>集中查看官网申请，并记录邀请、参与和关闭状态。</p>
        </div>
        <span className="waitlist-privacy-label">仅超级管理员可见</span>
      </div>

      <section className="waitlist-summary" aria-label="申请概览">
        <article><span>累计申请</span><strong>{summary.total}</strong><small>去重邮箱</small></article>
        <article><span>今日新增</span><strong>{summary.today}</strong><small>上海时区</small></article>
        <article><span>近 7 天</span><strong>{summary.last7Days}</strong><small>含今天</small></article>
        <article className={summary.pending > 0 ? "needs-action" : ""}><span>待跟进</span><strong>{summary.pending}</strong><small>需要处理</small></article>
      </section>

      <div className="toolbar waitlist-toolbar">
        <label className="search-box">
          <span aria-hidden="true">⌕</span>
          <span className="sr-only">搜索邮箱</span>
          <input value={query} onChange={(event) => { setQuery(event.target.value); setPage(1); }} placeholder="搜索申请邮箱" />
        </label>
        <label className="status-filter">
          <span className="sr-only">筛选跟进状态</span>
          <select value={status} onChange={(event) => { setStatus(event.target.value as WebsiteWaitlistStatus | ""); setPage(1); }}>
            <option value="">全部状态</option>
            {STATUS_OPTIONS.map(([value, label]) => <option key={value} value={value}>{label}</option>)}
          </select>
        </label>
        <span className="result-count">{loading ? "正在读取…" : `${pagination.total} 条申请`}</span>
      </div>

      {error ? <WaitlistError message={error} /> : null}

      <div className="waitlist-ops-layout">
        <section className="table-shell waitlist-table-shell" aria-label="内测申请名单">
          {loading ? <div className="table-skeleton"><span /><span /><span /></div> : (
            <table>
              <thead><tr><th>申请邮箱</th><th>状态</th><th>首次申请</th><th>最近申请</th><th>来源</th><th><span className="sr-only">操作</span></th></tr></thead>
              <tbody>
                {items.map((item) => (
                  <tr key={item.id} className={item.id === selectedId ? "selected" : ""}>
                    <td><strong className="waitlist-email">{item.email}</strong></td>
                    <td><span className={`waitlist-status status-${item.status.toLowerCase()}`}>{WEBSITE_WAITLIST_STATUS_LABELS[item.status]}</span></td>
                    <td>{formatWaitlistDate(item.firstAppliedAt)}</td>
                    <td>{formatWaitlistDate(item.lastAppliedAt)}</td>
                    <td><span className="waitlist-source">{item.source === "WEBSITE" ? "官网" : item.source}</span></td>
                    <td><button className="text-button waitlist-open" onClick={() => { setSelectedId(item.id); setSaved(false); }}>{item.id === selectedId ? "查看中" : "跟进"}</button></td>
                  </tr>
                ))}
                {!items.length ? <tr><td colSpan={6} className="empty-table-cell">{query || status ? "没有匹配的申请" : "官网还没有收到内测申请"}</td></tr> : null}
              </tbody>
            </table>
          )}
        </section>

        <aside className="waitlist-detail" aria-label="申请跟进详情">
          {selected ? (
            <form onSubmit={save}>
              <header>
                <div><span>申请人</span><strong>{selected.email}</strong></div>
                <button type="button" className="secondary-button waitlist-copy" onClick={() => void copyEmail()}>{copied ? "已复制" : "复制邮箱"}</button>
              </header>
              <dl>
                <div><dt>首次申请</dt><dd>{formatWaitlistDate(selected.firstAppliedAt)}</dd></div>
                <div><dt>最近申请</dt><dd>{formatWaitlistDate(selected.lastAppliedAt)}</dd></div>
              </dl>
              <label>
                <span>跟进状态</span>
                <select value={editStatus} onChange={(event) => { setEditStatus(event.target.value as WebsiteWaitlistStatus); setSaved(false); }}>
                  {STATUS_OPTIONS.map(([value, label]) => <option key={value} value={value}>{label}</option>)}
                </select>
              </label>
              <label>
                <span>内部备注</span>
                <textarea value={note} onChange={(event) => { setNote(event.target.value); setSaved(false); }} maxLength={2_000} rows={7} placeholder="例如：已发送第一轮测试邀请，等待回复。" />
                <small>{note.length} / 2000，仅管理后台可见</small>
              </label>
              {saved ? <div className="save-notice waitlist-save-success" role="status"><span>✓</span>跟进记录已保存</div> : null}
              <p className="waitlist-manual-note">保存只会记录跟进进度，不会自动向申请人发送邮件。</p>
              <button className="primary-button" disabled={saving || !hasChanges}>{saving ? "正在保存…" : "保存跟进记录"}</button>
            </form>
          ) : <div className="waitlist-detail-empty"><span>◎</span><strong>选择一条申请</strong><p>在左侧名单中点击“跟进”，查看并更新处理状态。</p></div>}
        </aside>
      </div>

      {pagination.totalPages > 1 ? (
        <div className="pagination">
          <span>第 {pagination.page} / {pagination.totalPages} 页，共 {pagination.total} 条</span>
          <div>
            <button className="secondary-button" disabled={pagination.page <= 1} onClick={() => setPage(pagination.page - 1)}>上一页</button>
            <button className="secondary-button" disabled={pagination.page >= pagination.totalPages} onClick={() => setPage(pagination.page + 1)}>下一页</button>
          </div>
        </div>
      ) : null}
    </div>
  );
}
